/* global debounce */

if (!customElements.get("typesense-autocomplete")) {
  // const COLLECTION_SUFFIX = "prod";
  const COLLECTION_SUFFIX = window.theme.typesense.collectionSuffix;
  const COLLECTIONS = {
    suggestions: `product_queries_${COLLECTION_SUFFIX}`,
    collections: `collections_${COLLECTION_SUFFIX}`,
    products: `products_${COLLECTION_SUFFIX}`,
    pages: `pages_${COLLECTION_SUFFIX}`,
  };

  class TypesenseAutocomplete extends HTMLElement {
    constructor() {
      super();

      this.input = null;
      this.deletionIntervalId = null;
      this.typingIntervalId = null;
      this.timeoutIds = null;
      this.timeoutId2 = null;
      this.timeoutId3 = null;
      this.instance = null;
      this.autocomplete = document.getElementById("autocomplete");
      this.autocompletePanel = document.getElementById("autocomplete-panel");
      this.overlay = document.getElementById("autocomplete-overlay");

      this.setupAutocomplete();
      this.debouncedSendQueryData = debounce(this.sendQueryData, 500);
    }

    async setupAutocomplete() {
      try {
        const client = new Typesense.SearchClient({
          nodes: [
            {
              host: window.theme.typesense.url,
              port: "443",
              protocol: "https",
            },
          ],
          apiKey: window.theme.typesense.apiKey,
          connectionTimeoutSeconds: 2,
        });
        const { autocomplete } = window["@algolia/autocomplete-js"];

        this.instance = autocomplete({
          classNames: {
            item: "autocomplete__hit",
          },
          container: "#autocomplete",
          ignoreCompositionEvents: true,
          panelContainer: "#autocomplete-panel",
          panelPlacement: "full-width",
          detachedMediaQuery: "none",
          getSources: async ({ query }) => {
            const searchRequests = {
              searches: [
                {
                  collection: COLLECTIONS.suggestions,
                  query_by: "q",
                  num_typos: 1,
                  filter_by: "count:>=3",
                  sort_by: "count:desc",
                  drop_tokens_threshold: 0,
                  limit: 4,
                },
                {
                  collection: COLLECTIONS.collections,
                  preset: "collections",
                  limit: 5,
                },
                {
                  collection: COLLECTIONS.products,
                  highlight_full_fields: "title",
                  preset: "max-weight",
                  limit: 6,
                },
                {
                  collection: COLLECTIONS.pages,
                  preset: "pages",
                  limit: 2,
                },
              ],
            };
            const commonParams = {
              q: query,
            };
            const { results } = await client.multiSearch.perform(
              searchRequests,
              commonParams
            );

            return results.map((result) => {
              const collectionName = result.request_params.collection_name;

              if (collectionName === COLLECTIONS.products && typeof debounce === 'function') {
                this.debouncedSendQueryData({
                  query,
                  noHits: result?.hits?.length ? result.hits.length === 0 : true
                });
              }

              return {
                sourceId: collectionName,
                getItemUrl: ({ item }) => {
                  return this.getItemUrl(item.document.q);
                },
                getItems() {
                  return result.hits;
                },
                getItemInputValue({ item }) {
                  return item.document.title;
                },
                templates: {
                  item: ({ item, html }) => {
                    // html is from the `htm` package. Docs: https://github.com/developit/htm

                    if (collectionName === COLLECTIONS.suggestions) {
                      const html_fragment = html`${item.highlight.q.snippet}`;
                      const url = this.getSuggestionUrl(item?.document?.q);

                      return html`
                        <div
                          data-id="${item.document.q}"
                          data-index="${collectionName}"
                          data-value="${item.document.q}"
                        >
                          <a class="autocomplete__link" href="${url}">
                            <span
                              class="autocomplete__link-text"
                              dangerouslySetInnerHTML=${{
                                __html: html_fragment,
                              }}
                            ></span>
                          </a>
                        </div>
                      `;
                    }

                    if (collectionName === COLLECTIONS.products) {
                      function renderStar(i, value) {
                        if (value >= i + 1)
                          return html`<span class="jdgm-star jdgm--on"></span>`;
                        if (value > i)
                          return html`<span
                            class="jdgm-star jdgm--half"
                          ></span>`;

                        return html`<span class="jdgm-star jdgm--off"></span>`;
                      }

                      const eventData = this.getEventDataFromHit(item.document);
                      const compareAt = item.document.compareAt;

                      return html`
                        <div
                          data-id="${item.document.q}"
                          data-index="${collectionName}"
                        >
                          <a
                            class="autocomplete-product autocomplete-product--hover-highlight"
                            href="/products/${item.document.handle}"
                          >
                            <script type="application/json">${JSON.stringify(eventData)}</script>
                            <div class="autocomplete-product__image-wrapper ${item.document.isOutOfStock ? 'autocomplete-product__image-wrapper--oos' : ''} ${!item.document.image.src ? "autocomplete-product__image-wrapper--placeholder" : ""}">
                              ${item.document.image.src ? html`<img
                                alt="${item.document.title}"
                                class="gg-image gg-image--contain gg-image--relative"
                                loading="eager"
                                src="${item.document.image.src}"
                              />` : this.getPlaceholderImageHtml(html)}
                            </div>
                            <div class="autocomplete-product__info">
                              <div>
                                <span
                                  class="autocomplete-product__price ${compareAt ? "autocomplete-product__price--highlighted" : ""}${item.document.priceVaries ? " autocomplete-product__price--from" : ""}"
                                  >$${item.document.minPrice.toFixed(2)}</span
                                >
                                ${compareAt
                                  ? html`<span
                                      class="autocomplete-product__compare-at"
                                      >$${compareAt.toFixed(
                                        2
                                      )}</span
                                    >`
                                  : ""}
                                ${!(item.document.compareAt && item.document.compareAt > item.document.minPrice) && Number(item.document.rrp) >= item.document.minPrice + 1
                                  ? html`<span
                                      class="autocomplete-product__rrp"
                                      >RRP: $${Number(item.document.rrp).toFixed(
                                        2
                                      )}</span
                                    >`
                                  : ""}
                              </div>
                              <div
                                class="autocomplete-product__title"
                                title="${item.document.title}"
                              >
                                ${item.document.title}
                              </div>
                              <div class="autocomplete-product__rating">
                                ${item.document.starRating > 1
                                  ? html`<div
                                      class="jdgm-prev-badge__stars"
                                      data-score="${item.document.rating}"
                                      aria-label="${item.document
                                        .starRating} stars"
                                    >
                                      ${[...Array(5)].map((_, i) =>
                                        renderStar(i, item.document.starRating)
                                      )}
                                    </div>`
                                  : null}
                              </div>
                            </div>
                          </a>
                        </div>
                      `;
                    }

                    return html`
                      <li
                        data-id="${item.document.id}"
                        data-index="${collectionName}"
                      >
                        <a
                          class="autocomplete__link"
                          href="/${collectionName.split("_")[0]}/${item.document
                            .handle}"
                          ><span class="autocomplete__link-text"
                            >${item.document.title}</span
                          ></a
                        >
                      </li>
                    `;
                  },
                  // noResults({ html }) {
                  //   if (collectionName !== `products_${COLLECTION_SUFFIX}`) return html`<div></div>`;

                  //   return 'No results found.';
                  // },
                },
              };
            });
          },
          render({ elements, render, html, state }, root) {
            function getSectionTitle(sourceId) {
              switch (sourceId) {
                case "collections":
                  return "Categories";
                case "pages":
                  return "Pages";
                case "products":
                  return "Product Suggestions";
                default:
                  return null;
              }
            }

            function modifySectionHTML(element, id) {
              const title = getSectionTitle(id);
              const titleHTML = title
                ? html`<h4 class="autocomplete__title">${title}</h4>`
                : undefined;
              const moreLink =
                id === "products"
                  ? html`<a
                      class="autocomplete__more"
                      href="/search?q=${state?.query || ""}"
                    >
                      View more
                      results${state?.query ? ` for "${state.query}"` : ""}</a
                    >`
                  : undefined;
              const updated = element
                ? {
                    ...element,
                    props: {
                      ...element.props,
                      className: undefined,
                      children: element.props.children.reduce((acc, cur) => {
                        if (!cur) return acc;

                        if (cur.type === "ul") {
                          return html`${titleHTML}${[
                            ...acc,
                            {
                              ...cur,
                              props: {
                                ...cur.props,
                                className: `autocomplete__hits autocomplete__hits--${id}`,
                              },
                            },
                          ]}`;
                        }

                        return [...acc, cur];
                      }, []),
                    },
                  }
                : element;

              if (!updated) {
                if (!theme.mediaMatches.lg) return null;

                return html`<li
                  class="autocomplete__category autocomplete__category--${id}"
                >
                  ${id !== "pages" ? titleHTML : null}${moreLink}
                </li>`;
              }

              return html`<li
                class="autocomplete__category autocomplete__category--${id}"
              >
                ${updated}${moreLink}
              </li>`;
            }

            render(
              html`<ul class="autocomplete">
                ${modifySectionHTML(
                  elements[COLLECTIONS.suggestions],
                  "suggestions"
                )}
                ${modifySectionHTML(
                  elements[COLLECTIONS.collections],
                  "collections"
                )}
                ${modifySectionHTML(elements[COLLECTIONS.products], "products")}
                ${modifySectionHTML(elements[COLLECTIONS.pages], "pages")}
              </ul>`,
              root
            );
          },
          renderNoResults({ render, html, state }, root) {
            return render(
              html`<div class="autocomplete">
                <div class="autocomplete__empty">
                  No results for "${state.query}"
                </div>
              </div>`,
              root
            );
          },
        });

        setTimeout(() => {
          this.afterSetup();
        }, 50);
      } catch (error) {
        throw error;
      }
    }

    sendQueryData(data) {
      gg.sendEvent({
        type: gg.eventTypes.SEARCH_QUERY,
        ...data
      })
    }

    getPlaceholderImageHtml(html) {
      return html`
        <div class="gg-image gg-image--relative">
          <svg class="gg-skeleton-image-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 358.771 256">
            <path d="M0 168c-6 64 50 80 104 80 86 0 132-33 141-82a67 67 0 001-14l-2-14c-19-55-135-42-130-70 2-12 13-17 29-17 7 0 14 2 18 6s6 9 6 17h39c18-56 50-66 102-66h40l-11 59c-30 0-41 0-45 37h39l-10 54h-39l-17 98h10l15-89h39l13-72h-39c2-9 4-14 7-16 5-3 14-3 27-3h8l14-76H148C99 0 34 18 24 77c-16 91 133 62 126 99-3 17-19 21-34 21-8 0-16-3-21-7-5-5-8-12-6-22z" />
          </svg>
        </div>
      `
    }

    getEventDataFromHit(hit) {
      return {
        id: hit.id,
        title: hit.title,
        vendor: hit.vendor,
        productType: hit.productType,
        variant: "", // None available for impressions
        minPrice: hit.minPrice,
        variant_id: "", // None available for impressions
        compareAt: hit.compareAt || "",
        image: hit.image?.src ? {
          src: hit.image.src
        } : ""
      }
    }

    afterSetup() {
      this.input = this.autocomplete.querySelector("input.aa-Input");
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('autocorrect', 'off');
      this.input.setAttribute('autocapitalize', 'off');
      this.input.setAttribute('spellcheck', 'false');

      if (!this.input) return;

      this.inputListener();
      this.typeInPlaceholders();
      this.resultClickListener();
    }

    resultClickListener() {
      this.autocompletePanel.addEventListener("click", (e) => {
        const isProductHit = !!e.target.closest('.autocomplete__hits--products');
        
        if (e.target.classList.contains('autocomplete__hits')) return;
        if (isProductHit) {
          e.preventDefault();
          
          const el = "a.autocomplete-product";
          const target = e.target.tagName === "A" ? e.target : (e.target.closest(el) || e.target.querySelector(el));

          if (target) {
            const hitJSON = target.querySelector('[type="application/json"]');
            const hit = hitJSON ? JSON.parse(hitJSON.textContent) : null;

            if (hit) {
              gg.sendEvent({
                data: {
                  hit,
                  position: target.getAttribute("data-position"),
                  index: COLLECTIONS.products
                },
                type: gg.eventTypes.CLICK
              });
            }
            
            if (e.metaKey || e.ctrlKey) {
              window.open(target.href, '_blank', 'noopener')
            } else {
              location.href = target.href;
            }
          }
        }
      });
    }

    inputListener() {
      this.input.addEventListener("focus", () => {
        if (window.matchMedia("(max-width: 767px)").matches) {
          this.overlay.dataset.visible = "true";
        } else {
          clearInterval(this.deletionIntervalId);
          clearInterval(this.typingIntervalId);
          clearTimeout(this.timeoutId);
          clearTimeout(this.timeoutId2);
          clearTimeout(this.timeoutId3);
        }
      });

      this.input.addEventListener("blur", () => {
        this.overlay.dataset.visible = "false";
        this.typeInPlaceholders();
      });

      this.input.closest("form").addEventListener("submit", (e) => {
        const query = this.input.value;
        window.location.href = this.getSuggestionUrl(query);
      });
    }

    typeInPlaceholders() {
      this.input.setAttribute(
        "placeholder",
        this.autocomplete.dataset.placeholderOne
      );

      if (
        this.input.dataset.placeholderPromptsMob !== "true" &&
        window.matchMedia("(max-width: 767px)").matches
      ) {
        return;
      }

      const typingSpeed = this.autocomplete.dataset.typingSpeed || 100;
      const deletingSpeed = this.autocomplete.dataset.deletingSpeed || 60;
      const delayAfterDeleting =
        this.autocomplete.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete =
        this.autocomplete.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped =
        this.autocomplete.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.autocomplete.dataset.placeholderTwo) {
        placeholders.push(this.autocomplete.dataset.placeholderTwo);
      }

      if (this.autocomplete.dataset.placeholderThree) {
        placeholders.push(this.autocomplete.dataset.placeholderThree);
      }

      if (this.autocomplete.dataset.placeholderOne) {
        placeholders.push(this.autocomplete.dataset.placeholderOne);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute("placeholder");

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder =
            currentPlaceholder.length >= 3 &&
            placeholder.startsWith(currentPlaceholder)
              ? placeholder.replace(currentPlaceholder, "")
              : placeholder;

          this.typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute("placeholder");
            this.input.setAttribute(
              "placeholder",
              currentPlaceholder + nextPlaceholder.charAt(0)
            );
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(this.typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute("placeholder");
          this.deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(
              0,
              prevPlaceholder.length - 1
            );
            this.input.setAttribute("placeholder", newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (
              prevPlaceholder.length === 0 ||
              (prevPlaceholder.length >= 3 &&
                nextPlaceholder.startsWith(prevPlaceholder))
            ) {
              resolve();
              clearInterval(this.deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          this.timeoutId2 = setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              this.timeoutId3 = setTimeout(
                showNextPlaceholder.bind(this),
                delayAfterWordTyped
              );
            });
          }, delayAfterDeleting);
        });
      };

      this.timeoutId = setTimeout(
        showNextPlaceholder.bind(this),
        delayBeforeFirstDelete
      );
    }

    getItemUrl(query) {
      return `/search?q=${encodeURIComponent(query)}`;
    }

    getSuggestionUrl(suggestion) {
      const redirectsRaw = window?.theme?.typesense?.searchRedirects;
      const redirects = redirectsRaw ? JSON.parse(redirectsRaw) : null;
      const matchingCollectionUrl = redirects ? redirects[suggestion] : null;

      return matchingCollectionUrl || this.getItemUrl(suggestion);
    }
  }

  customElements.define("typesense-autocomplete", TypesenseAutocomplete);
}
